import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useMountedState } from 'react-use';
import { useAppThemeContext } from '../../contexts/AppThemeContext';

interface I18nProps {
    userSelectedLang?: string;
    switchLanguage: (loading: string) => void;
}
export const I18nContext = React.createContext<I18nProps>({
    switchLanguage: () => {},
});

export const useI18nContext = () => React.useContext(I18nContext);

export const I18nProvider: React.FunctionComponent = ({ children }) => {
    const { i18n } = useTranslation();
    const isMounted = useMountedState();

    const [userSelectedLang, setUserSelectedLang] = React.useState<string>();
    const [error, setError] = React.useState<Error>();
    const currentUSVariantRef = React.useRef<string>();

    const { setIsRtl } = useAppThemeContext();

    const switchLanguage = (language: string) => {
        window.localStorage.setItem('selectedLocale', language);
        setUserSelectedLang(language);
    };

    React.useEffect(() => {
        const hasResourceBundle = (language: string): boolean => {
            const defaultNs = i18n.options.defaultNS ?? 'translations';
            if (i18n.hasResourceBundle(language, defaultNs)) return true;

            if (
                language.includes('-') &&
                i18n.hasResourceBundle(language.split('-')[0], defaultNs)
            )
                return true;

            if (!i18n.options.fallbackLng) return false;

            const fallBacks = i18n.options.fallbackLng as string[];
            return fallBacks.some(lng =>
                i18n.hasResourceBundle(lng, defaultNs),
            );
        };

        const throwWhenTranslationsNotLoaded = (language: string) => {
            if (!hasResourceBundle(language)) {
                throw new Error(
                    `Unable to load application translation file (${language}).`,
                );
            }
        };

        const isUSBundle = (language?: string) =>
            language?.startsWith('en-US-');

        const getI18nLanguage = (language: string) => {
            // en-US variants use 'en-US' as the i18next language
            // (the suffix is handled by loadPath reading localStorage)
            if (isUSBundle(language)) return 'en-US';
            return language;
        };

        if (userSelectedLang) {
            const targetLang = getI18nLanguage(userSelectedLang);

            if (isUSBundle(userSelectedLang)) {
                // Skip if the exact same variant is already active
                if (
                    currentUSVariantRef.current === userSelectedLang &&
                    i18n.language === 'en-US'
                ) {
                    return;
                }
                // Reload en-US resources so the http-backend re-fetches
                // via loadPath (which reads localStorage for the k12/pro
                // suffix). reloadResources uses { reload: true } internally,
                // bypassing the BackendConnector's "already loaded" cache.
                // changeLanguage emits 'languageChanged' which triggers
                // react-i18next to re-render with the updated translations.
                currentUSVariantRef.current = userSelectedLang;
                const targetVariant = userSelectedLang;
                i18n.reloadResources(['en-US'], ['translation'])
                    .then(() => {
                        // Guard: only proceed if this variant is still the target
                        if (currentUSVariantRef.current === targetVariant) {
                            return i18n.changeLanguage('en-US');
                        }
                    })
                    .then(() => {
                        // Guard: verify variant is still current before validation
                        if (currentUSVariantRef.current === targetVariant) {
                            throwWhenTranslationsNotLoaded(i18n.language);
                        }
                    })
                    .catch(error => {
                        if (isMounted()) {
                            setError(error);
                        }
                    });
            } else if (targetLang !== i18n.language) {
                // Switching to a different non-US language
                currentUSVariantRef.current = undefined;
                i18n.changeLanguage(targetLang)
                    .then(() => {
                        throwWhenTranslationsNotLoaded(i18n.language);
                    })
                    .catch(error => {
                        if (isMounted()) {
                            setError(error);
                        }
                    });
            }
        } else {
            // first i18n init
            throwWhenTranslationsNotLoaded(i18n.language);
            const selectedLanguage =
                window.localStorage.getItem('selectedLocale') ?? i18n.language;
            setUserSelectedLang(selectedLanguage);
            if (isUSBundle(selectedLanguage)) {
                currentUSVariantRef.current = selectedLanguage;
            }
        }
    }, [i18n, userSelectedLang, isMounted]);

    if (error) {
        throw error;
    }

    React.useEffect(() => {
        i18n.on('languageChanged', () => {
            setIsRtl(i18n.dir() === 'rtl');
        });
        setIsRtl(i18n.dir() === 'rtl');
    }, [i18n, setIsRtl]);

    return (
        <I18nContext.Provider value={{ userSelectedLang, switchLanguage }}>
            <Helmet htmlAttributes={{ lang: i18n.language }} />
            {children}
        </I18nContext.Provider>
    );
};
